import {
  Agreement01Icon,
  Building03Icon,
  Analytics01Icon,
  Calendar03Icon,
  DashboardSquare01Icon,
  Settings02Icon,
  Mail01Icon,
  Notification01Icon,
  PresentationBarChart01Icon,
  QrCodeIcon,
  ThumbsUpIcon,
  RefreshIcon,
  UserGroupIcon,
  UserMultiple02Icon,
  WhatsappIcon,
} from '@hugeicons/core-free-icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The Connection agent: Bridged's matching engine suggests who should meet at an event, the
 * organiser reviews and publishes, and attendees choose and book in the Connection app.
 * An engine is built for one format; the agent picks one engine per format it runs.
 * Format values are an agent slot's, as the API takes them: 'one_to_one' | 'one_to_many' |
 * 'session_recs'. A matching engine calls session recommendations 'sessions': map between the two
 * with `engineFormatOf` and `agentFormatOf`.
 */
export const CONNECTION_FORMATS = [
  {
    value: 'one_to_one',
    title: 'One to one',
    description: 'Pairs two people: delegates with sponsors, delegates with each other, or both.',
    icon: Agreement01Icon,
  },
  {
    value: 'one_to_many',
    title: 'One to many',
    description: 'Groups delegates into round tables and hosted sessions, within each capacity.',
    icon: UserGroupIcon,
  },
  {
    value: 'session_recs',
    title: 'Session recommendations',
    description: 'Suggests the programme sessions each delegate should add to their day.',
    icon: PresentationBarChart01Icon,
  },
  {
    value: 'clustering',
    title: 'Clustering',
    description: 'Finds natural groups across the attendee list.',
    icon: UserMultiple02Icon,
    disabled: true,
    disabledReason: 'Coming soon',
  },
];

export const CONNECTION_PAIRINGS = [
  {
    value: 'delegate_sponsor',
    title: 'Delegate and sponsor',
    description: 'Meetings at the sponsor’s stand. The sponsor’s calendar sets the times.',
  },
  {
    value: 'delegate_delegate',
    title: 'Delegate and delegate',
    description: 'Peer introductions, continued by email.',
  },
];

/** An agent slot's format as a matching engine names it: 'session_recs' → 'sessions'. */
export const engineFormatOf = format => (format === 'session_recs' ? 'sessions' : format);

/** A matching engine's format as an agent slot names it: 'sessions' → 'session_recs'. */
export const agentFormatOf = format => (format === 'sessions' ? 'session_recs' : format);

export const getConnectionFormatLabel = value =>
  CONNECTION_FORMATS.find(format => format.value === agentFormatOf(value))?.title || value;

export const getConnectionPairingLabel = value =>
  CONNECTION_PAIRINGS.find(pairing => pairing.value === value)?.title || value;

/**
 * One slot per format the agent runs, and for one to one one per pairing (D2): an agent can run
 * delegate and sponsor meetings and delegate and delegate introductions together. The key names a
 * slot everywhere: `one_to_one:delegate_sponsor`, `one_to_one:delegate_delegate`, `session_recs`.
 */
export const connectionSlotKey = slot =>
  slot?.format === 'one_to_one' ? `one_to_one:${slot.pairing || 'delegate_sponsor'}` : slot?.format;

/** "One to one (delegate and sponsor)", "Session recommendations". */
export const connectionSlotLabel = slot =>
  slot?.format === 'one_to_one'
    ? `${getConnectionFormatLabel(slot.format)} (${getConnectionPairingLabel(slot.pairing || 'delegate_sponsor').toLowerCase()})`
    : getConnectionFormatLabel(slot?.format);

/** The agent's slots, once each, as the Matches tab lists them: what the other views can be narrowed to. */
export const connectionFormatSlots = agent => {
  const seen = new Set();
  return (agent?.formats || []).filter(slot => {
    const key = connectionSlotKey(slot);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** The slot a key names as `{ format, pairing, label }` for the API, or null for every format. */
export const connectionFormatFilter = (agent, key) => {
  const slot = connectionFormatSlots(agent).find(item => connectionSlotKey(item) === key);
  if (!slot) return null;
  const format = agentFormatOf(slot.format);
  return {
    key,
    format,
    pairing: format === 'one_to_one' ? slot.pairing || 'delegate_sponsor' : null,
    label: connectionSlotLabel(slot),
  };
};

/** A query's argument: the agent id alone for every format (as other views ask), or narrowed to one. */
export const connectionFormatArg = (agentId, filter) =>
  filter ? { agentId, format: filter.format, pairing: filter.pairing || undefined } : agentId;

/** Who sends the first request: the other side accepts or passes. */
export const CONNECTION_INITIATORS = [
  {
    value: 'delegate',
    title: 'Delegates pick first',
    description: 'Delegates choose sponsors from their suggestions; sponsors accept or pass.',
  },
  {
    value: 'sponsor',
    title: 'Sponsors pick first',
    description: 'Sponsors choose delegates from their suggestions; delegates accept or pass.',
  },
  {
    value: 'both',
    title: 'Anyone picks first',
    description:
      'Both sides get suggestions and can send requests; whoever is asked accepts or passes. Sponsors see the delegates matched with them.',
  },
];

/**
 * The report's funnel words after "Signed in", for the side that picks first and the side that answers. With
 * anyone picking first both sides ask and answer, so each counts the people in a request, and those who agreed.
 */
export const connectionJourneyWords = initiator =>
  initiator === 'both'
    ? { asker: ['In a request', 'Agreed to meet'], answerer: ['In a request', 'Agreed to meet'] }
    : {
        asker: ['Asked for a meeting', 'Got a yes'],
        answerer: ['Received a request', 'Said yes to someone'],
      };


export const CONNECTION_NOTE_OPTIONS = [
  { value: 'off', label: 'No note' },
  { value: 'optional', label: 'Optional note' },
  { value: 'required', label: 'Note required' },
];

export const DEFAULT_CONNECTION_RULES = {
  initiator: 'delegate',
  pickCap: 8,
  // Accepts per responder (the app's responderConfirmCap): how many requests each person on the
  // other side can say yes to. Empty means no limit.
  confirmCap: null,
  note: 'optional',
  showScore: true,
  opensAt: null,
  closesAt: null,
  feedback: true,
  // Delegates see a sponsor as "Rep Name · Company" instead of the company alone.
  showRepNames: false,
  // An Exhibitors tab in the Connection app, listing every sponsoring company.
  exhibitors: false,
  // People search: the picking side can also find anyone on the other side and ask them, outside
  // their matches. Those requests have their own limit (empty means no limit) and reach the other
  // side quietly, in the app only.
  search: false,
  searchCap: 3,
  // Attendees choose the round tables they join in the hub (tables not invite only); off means
  // the events team seats them. `roundtableCap` is how many one person can join.
  roundtableChoice: false,
  roundtableCap: 2,
  // Sponsor companies: sponsors grouped by company name, each with reps and a lead contact.
  companies: false,
  // Lead scanning: everyone scans badges in the app (each person's link is their badge's QR), and
  // a personal link no longer signs anyone in by itself. The Scans tab shows only with it on.
  leadScanning: false,
  // Attendees can change their meeting times (a sponsor's open times, meeting length and gap). Off:
  // the organisers set them: the meeting windows (or the meeting hours) at the default length and gap.
  attendeeTimes: true,
};

/** Requests from search per person: an agent saved before the setting has the API's default, 3; null is no limit. */
export const connectionSearchCap = rules =>
  rules?.searchCap === undefined ? DEFAULT_CONNECTION_RULES.searchCap : rules.searchCap;

/**
 * What delegates are recommended in one to one with sponsors (a format slot's `recommend`): each rep,
 * or each sponsor company once. Companies need Sponsor companies on in the rules.
 */
export const CONNECTION_RECOMMEND_OPTIONS = [
  { value: 'rep', label: 'Each rep' },
  { value: 'company', label: 'Each company' },
];

/**
 * How meetings are laid out on sponsors' calendars, as the Connection app keeps them: the lengths
 * sponsors can offer and the default (`durationOptions`, `defaultDuration`), the gaps between
 * meetings likewise (`bufferOptions`, `defaultBuffer`), and the meeting hours.
 */
export const DEFAULT_CONNECTION_MEETINGS = {
  durationOptions: [15, 20, 30],
  defaultDuration: 20,
  bufferOptions: [5, 10],
  defaultBuffer: 10,
  dayStart: '09:00',
  dayEnd: '17:00',
};

export const CONNECTION_MEETING_DURATIONS = [15, 20, 30, 45];
export const CONNECTION_MEETING_BUFFERS = [0, 5, 10, 15];

/** An agent's meeting layout with today's fields, also from one saved before lengths became lists. */
export const normaliseConnectionMeetings = (meetings = {}) => {
  const defaultDuration = meetings.defaultDuration ?? meetings.duration ?? 20;
  const defaultBuffer = meetings.defaultBuffer ?? meetings.buffer ?? 10;
  return {
    ...DEFAULT_CONNECTION_MEETINGS,
    ...meetings,
    durationOptions: meetings.durationOptions?.length
      ? meetings.durationOptions
      : [defaultDuration],
    defaultDuration,
    bufferOptions: meetings.bufferOptions?.length ? meetings.bufferOptions : [defaultBuffer],
    defaultBuffer,
  };
};

/**
 * What's wrong with the meeting windows (`meetings.windows`), in the portal's words, or null. Empty
 * is fine: meetings then run in the meeting hours every day.
 */
export const connectionMeetingWindowsProblem = windows => {
  const rows = windows || [];
  if (rows.some(w => !w.date || !w.start || !w.end))
    return 'Give every meeting window a day, a start and an end';
  if (rows.some(w => w.start >= w.end)) return 'Each meeting window must end after it starts';
  return null;
};

/**
 * Addresses the Connection app can't give an agent: its own top-level files and folders (assets/,
 * sw.js, manifest.webmanifest, icon.svg, /healthz) and words kept for its routes.
 */
const RESERVED_APP_URLS = [
  'admin',
  'api',
  'app',
  'assets',
  'auth',
  'bridged',
  'clients',
  'favicon',
  'health',
  'healthz',
  'icon',
  'icons',
  'index',
  'login',
  'manifest',
  'new',
  'next',
  'public',
  'robots',
  'static',
  'sw',
  'www',
];
export const CONNECTION_APP_URL_MAX = 40;

/** Why a Connection app address (already letters, digits and hyphens) can't be used, or null. */
export const connectionAppUrlProblem = slug => {
  if (slug.length > CONNECTION_APP_URL_MAX)
    return `Keep it to ${CONNECTION_APP_URL_MAX} characters or fewer.`;
  if (RESERVED_APP_URLS.includes(slug))
    return `“${slug}” is kept for the app itself. Pick another.`;
  return null;
};

/**
 * Whether the agent runs a format: 'one_to_many' (round tables), 'session_recs' (sessions) or
 * 'delegate_delegate' (peer introductions, a one to one pairing).
 */
export const connectionHasFormat = (agent, format) =>
  (agent?.formats || []).some(slot =>
    format === 'delegate_delegate'
      ? slot?.format === 'one_to_one' && slot?.pairing === 'delegate_delegate'
      : agentFormatOf(slot?.format) === format
  );

/**
 * Whether an agent with these format slots has sponsors: every format but delegates meeting each
 * other brings them in. No formats picked yet counts as yes, so nothing is hidden too early.
 */
export const connectionHasSponsors = formats =>
  !formats?.length ||
  formats.some(slot => slot.format !== 'one_to_one' || slot.pairing !== 'delegate_delegate');

/**
 * What looks wrong in the meeting windows without stopping a save, for the Rules step to say
 * inline: windows on the same day that overlap, and windows reaching outside the meeting hours
 * (the windows replace the hours, so meetings can then be booked outside them).
 */
export const connectionMeetingWindowsWarnings = (windows, dayStart, dayEnd) => {
  const rows = (windows || []).filter(w => w.date && w.start && w.end && w.start < w.end);
  const span = w => `${w.label ? `${w.label} ` : ''}${w.start}–${w.end}`;
  const warnings = [];
  const sorted = [...rows].sort((a, b) =>
    `${a.date} ${a.start}`.localeCompare(`${b.date} ${b.start}`)
  );
  sorted.forEach((w, i) => {
    const next = sorted[i + 1];
    if (next && next.date === w.date && next.start < w.end)
      warnings.push(
        `${dayjs(w.date).format('ddd D MMM')}: ${span(w)} and ${span(next)} overlap. Make them one window or move one.`
      );
  });
  if (dayStart && dayEnd) {
    const outside = sorted.filter(w => w.start < dayStart || w.end > dayEnd);
    if (outside.length)
      warnings.push(
        `${outside.map(w => `${dayjs(w.date).format('ddd D MMM')} ${span(w)}`).join('; ')} ${
          outside.length === 1 ? 'goes' : 'go'
        } outside the meeting hours (${dayStart}–${dayEnd}). Windows replace the hours, so meetings can be booked then.`
      );
  }
  return warnings;
};

/**
 * Meeting windows as the rules write them, in day and time order: "Tue 3 Nov · Networking,
 * 12:00–15:00". A window without a label reads "Tue 3 Nov, 12:00–15:00".
 */
export const connectionMeetingWindowsText = windows =>
  [...(windows || [])]
    .sort((a, b) => `${a.date} ${a.start}`.localeCompare(`${b.date} ${b.start}`))
    .map(w => {
      const day = w.date ? dayjs(w.date).format('ddd D MMM') : 'Any day';
      return `${day}${w.label ? ` · ${w.label}` : ''}, ${w.start}–${w.end}`;
    });

/** The event's days (YYYY-MM-DD) from its start date and length in days. */
export const connectionEventDays = event => {
  if (!event?.date) return [];
  const first = dayjs(event.date);
  if (!first.isValid()) return [];
  const count = Math.max(1, Math.min(Number(event.duration) || 1, 60));
  return Array.from({ length: count }, (_, i) => first.add(i, 'day').format('YYYY-MM-DD'));
};

/** The meeting length the events team books by default. */
export const connectionMeetingLength = meetings =>
  normaliseConnectionMeetings(meetings).defaultDuration;

/** Attendee type, read from the imported contact (HubSpot's `attendee_type`, or a CSV column). */
export const getAttendeeRole = metadata => {
  const value = Object.entries(metadata || {})
    .filter(([key]) => /attendee|role|ticket|type/i.test(key))
    .map(([, v]) => String(v || ''))
    .join(' ');
  return /sponsor|exhibitor|partner/i.test(value) ? 'sponsor' : 'delegate';
};

export const CONNECTION_MATCH_STATUS = {
  suggested: { label: 'Suggested', variant: 'secondary' },
  requested: { label: 'Requested', variant: 'sky' },
  accepted: { label: 'Accepted', variant: 'success' },
  declined: { label: 'Declined', variant: 'outline' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
};

/** A roundtable invitation (one to many): the delegate hasn't answered, said yes, or said no. */
export const CONNECTION_INVITATION_STATUS = {
  suggested: { label: 'Invited', variant: 'sky' },
  accepted: { label: 'Accepted', variant: 'success' },
  declined: { label: 'Declined', variant: 'outline' },
};

/** The agent's sessions (Agenda): format, whose schedule a session is on, and its tag in the app. */
export const CONNECTION_SESSION_FORMATS = [
  'Keynote',
  'Seminar',
  'Panel',
  'Fireside',
  'Workshop',
  'Roundtable',
  'Networking',
  'Briefing',
];

export const CONNECTION_SESSION_AUDIENCES = [
  { value: 'delegates', label: 'Delegates’ schedule' },
  { value: 'sponsors', label: 'Sponsors’ schedule' },
  { value: 'everyone', label: 'Everyone’s schedule' },
];

export const CONNECTION_SESSION_TAGS = [
  { value: 'programme', label: 'Programme' },
  { value: 'suggested', label: 'Suggested' },
  { value: 'hosted_lunch', label: 'Hosted lunch' },
];

export const CONNECTION_TIMEZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Asia/Dubai',
  'Asia/Singapore',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
];

/**
 * Times on the event's clock. The API, the hubs and the emails all work on the agent's
 * `timezone`, so every Connections screen shows and reads times there, whatever zone the
 * viewer's browser is in. `tz` is the agent's `timezone`; the API's default when it is missing.
 */
export const CONNECTION_DEFAULT_TIMEZONE = 'Europe/London';

const zoned = (iso, tz) => {
  try {
    return dayjs(iso).tz(tz || CONNECTION_DEFAULT_TIMEZONE);
  } catch {
    return dayjs(iso).tz(CONNECTION_DEFAULT_TIMEZONE);
  }
};

/** Format an instant on the event's clock: `eventTime(iso, agent?.timezone, 'HH:mm')`. */
export const eventTime = (iso, tz, pattern = 'ddd D MMM, HH:mm') =>
  iso ? zoned(iso, tz).format(pattern) : '';

/**
 * Where a new meeting window starts: on the next event day that isn't over yet (on the event's
 * clock), never in the past. After the last window when that is still to come; today, from the next
 * quarter hour; a later day, from the meeting hours' start. `days` are the event's days (YYYY-MM-DD),
 * none when they aren't known (then today, or tomorrow once today's hours are over).
 */
export const nextMeetingWindow = ({
  rows = [],
  days = [],
  tz,
  now = new Date(),
  dayStart = '09:00',
  dayEnd = '17:00',
}) => {
  const today = eventTime(now.toISOString(), tz, 'YYYY-MM-DD');
  const clock = zoned(now.toISOString(), tz);
  const minutes = clock.hour() * 60 + clock.minute();
  const quarter = Math.ceil((minutes + 1) / 15) * 15;
  const nextQuarter =
    quarter < 24 * 60
      ? `${String(Math.floor(quarter / 60)).padStart(2, '0')}:${String(quarter % 60).padStart(2, '0')}`
      : null;
  const later = (a, b) => (a > b ? a : b);
  const last = rows[rows.length - 1];
  // Right after the last window, when that is still ahead.
  if (
    last?.date &&
    last.end &&
    (last.date > today || (last.date === today && nextQuarter && last.end >= nextQuarter))
  )
    return { date: last.date, start: last.end };
  const candidates = (
    days.length ? [...days].sort() : [today, dayjs(today).add(1, 'day').format('YYYY-MM-DD')]
  ).filter(date => date >= today);
  for (const date of candidates) {
    if (date > today) return { date, start: dayStart || '12:00' };
    if (nextQuarter && nextQuarter < (dayEnd || '24:00'))
      return { date, start: later(nextQuarter, dayStart || '00:00') };
  }
  // The event is over: its last day, as before.
  return {
    date: last?.date || days[days.length - 1] || today,
    start: last?.end || dayStart || '12:00',
  };
};

/**
 * An event's dates with weekdays and an en dash, on the event's clock: "Tue 3 – Wed 4 Nov 2026",
 * "Mon 30 Nov – Tue 1 Dec 2026", or "Tue 3 Nov 2026" for one day. `days` is how many days it runs.
 */
export const connectionDateRange = (startsAt, days, tz) => {
  if (!startsAt) return '';
  const start = zoned(startsAt, tz);
  const count = Math.max(1, Number(days) || 1);
  const end = start.add(count - 1, 'day');
  if (count === 1) return start.format('ddd D MMM YYYY');
  const from = start.isSame(end, 'year')
    ? start.format(start.isSame(end, 'month') ? 'ddd D' : 'ddd D MMM')
    : start.format('ddd D MMM YYYY');
  return `${from} – ${end.format('ddd D MMM YYYY')}`;
};

const WALL_CLOCK = 'YYYY-MM-DDTHH:mm:ss';

/** An instant as a date-picker value that shows the event's wall-clock time. */
export const toEventPicker = (iso, tz) => (iso ? dayjs(zoned(iso, tz).format(WALL_CLOCK)) : null);

/** A date-picker value read as the event's wall-clock time, returned as an ISO instant. */
export const fromEventPicker = (value, tz) => {
  if (!value || !value.isValid?.()) return null;
  try {
    return dayjs.tz(value.format(WALL_CLOCK), tz || CONNECTION_DEFAULT_TIMEZONE).toISOString();
  } catch {
    return dayjs.tz(value.format(WALL_CLOCK), CONNECTION_DEFAULT_TIMEZONE).toISOString();
  }
};

export const CONNECTION_MEETING_STATUS = {
  requested: { label: 'Waiting for an answer', variant: 'sky' },
  accepted: { label: 'Needs a time', variant: 'warning' },
  // Both said yes and are agreeing a time between themselves (the app's `booking`).
  booking: { label: 'Booking externally', variant: 'sky' },
  booked: { label: 'Booked', variant: 'success' },
  held: { label: 'Held', variant: 'secondary' },
  declined: { label: 'Declined', variant: 'outline' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
};

/** Who a matching run is for. Selected attendees: a checklist; one to one brings every sponsor in. */
export const CONNECTION_RUN_SCOPES = [
  {
    value: 'new',
    title: 'Only people without suggestions',
    description: 'For late registrants. Everyone else keeps what they have.',
  },
  {
    value: 'selected',
    title: 'Selected attendees',
    description: 'Pick delegates from the list. Every sponsor is matched with them.',
  },
  {
    value: 'all',
    title: 'Everyone',
    description: 'Adds new suggestions for everyone. Nothing published is changed or removed.',
  },
];

/** A run's status: `running`, then `finished` or `failed` (`completed` is the prototype's word for finished). */
export const CONNECTION_RUN_STATUS = {
  running: { label: 'Matching', variant: 'sky' },
  finished: { label: 'Finished', variant: 'success' },
  completed: { label: 'Finished', variant: 'success' },
  failed: { label: 'Didn’t finish', variant: 'destructive' },
};

/** The matching service's stages, as the progress card names them (`runningRun.progress.stage`). */
export const CONNECTION_RUN_STAGES = {
  queued: 'Waiting to start',
  fetching: 'Reading the attendees',
  preparing: 'Preparing the attendees',
  scoring: 'Scoring each pair',
  solving: 'Choosing the best matches',
  writing: 'Saving the matches',
};

/** "Matching didn't finish: {reason}", or the reason as it is when it says so itself ("Matching stopped before it finished…"). */
export const connectionRunFailedText = run => {
  if (!run?.failedReason) return 'Matching didn’t finish.';
  return /^matching\b/i.test(run.failedReason)
    ? run.failedReason
    : `Matching didn’t finish: ${run.failedReason}`;
};

/** When in the event a delivery goes. Today's schedule is on the day only; HubSpot before the event only. */
export const CONNECTION_DELIVERY_TIMINGS = [
  {
    value: 'pre_event',
    label: 'Before the event',
    description: 'Before the doors open: get people into the app and choosing.',
  },
  {
    value: 'on_day',
    label: 'On the day',
    description: 'At the event: today’s meetings, rooms and stands.',
  },
];

/** What a delivery is for. Link purposes send each person their own Connection app link. */
export const CONNECTION_DELIVERY_PURPOSES = [
  {
    value: 'invite',
    title: 'Invite to the app',
    description: 'Each person’s link to their Connection app, the first time they get it.',
    defaultAudience: 'not_received',
    carriesLink: true,
    subject: '{first_name}, your meetings at {event_name}',
    // {next_step} follows each person's side: the side that picks first chooses who to meet; the
    // side that answers sees who wants to meet them and sets their times.
    message:
      'Your Connection app for {event_name} is ready. We’ve suggested people worth meeting: open your link to {next_step}.',
    pushTitle: 'Your meetings are ready',
  },
  {
    value: 'reminder',
    title: 'Reminder',
    description: 'A nudge for anyone who hasn’t opened their link or picked yet.',
    defaultAudience: 'not_signed_in',
    carriesLink: true,
    subject: 'Your meetings at {event_name} are waiting',
    message:
      'You haven’t chosen who to meet yet. Your suggestions are waiting in your Connection app: it takes two minutes.',
    pushTitle: 'Your matches are waiting',
  },
  {
    value: 'results',
    title: 'New suggestions',
    description: 'After a new run is published: there are more people to meet.',
    defaultAudience: 'everyone',
    carriesLink: true,
    subject: 'New people to meet at {event_name}',
    message: 'We’ve added new suggestions to your Connection app. Have a look before they fill up.',
    pushTitle: 'New people to meet',
  },
  {
    value: 'schedule',
    title: 'Today’s schedule',
    description: 'On the day: each person’s meetings, times and places.',
    defaultAudience: 'meeting_today',
    carriesLink: true,
    timings: ['on_day'],
    subject: 'Your meetings today at {event_name}',
    // Each person's day (meetings, sessions and round tables, with times and places) follows the message.
    message: 'Here’s your day: {meetings_today_text}, the first at {first_meeting}.',
    listsDay: true,
    pushTitle: 'Your day at {event_name}',
  },
  {
    value: 'announcement',
    title: 'Announcement',
    description: 'A short update to everyone or a group, such as a room change. No personal link.',
    defaultAudience: 'everyone',
    carriesLink: false,
    subject: 'An update from {event_name}',
    message: '',
    pushTitle: '',
  },
  {
    value: 'pending_actions',
    title: 'Pending actions email',
    description: 'One email to everybody with something waiting in the Connection app.',
    internal: true,
  },
];

/** Who gets a delivery. The activity groups are the live app's broadcast segments. */
export const CONNECTION_DELIVERY_AUDIENCE_GROUPS = [
  { value: 'groups', label: 'Groups' },
  { value: 'link', label: 'Their link' },
  { value: 'activity', label: 'What they’ve done so far' },
  { value: 'pick', label: 'One by one' },
];

export const CONNECTION_DELIVERY_AUDIENCES = [
  { value: 'everyone', label: 'Everyone', group: 'groups' },
  { value: 'delegates', label: 'Delegates', group: 'groups' },
  { value: 'sponsors', label: 'Sponsors', group: 'groups' },
  { value: 'not_received', label: 'Haven’t had their link yet', group: 'link' },
  { value: 'not_signed_in', label: 'Haven’t opened their link', group: 'link' },
  { value: 'sponsors_no_times', label: 'Sponsors without times', group: 'link' },
  { value: 'with_meetings', label: 'Have a meeting booked', group: 'activity' },
  { value: 'no_meetings', label: 'No meetings booked yet', group: 'activity' },
  // The day an on-the-day delivery goes (Today's schedule's group); the two above count any day.
  { value: 'meeting_today', label: 'Have a meeting today', group: 'activity' },
  { value: 'awaiting_reply', label: 'Have a request waiting for their reply', group: 'activity' },
  {
    value: 'needs_time',
    label: 'Have a yes but still need to pick a time',
    group: 'activity',
  },
  { value: 'pick', label: 'Pick people', group: 'pick' },
  { value: 'pending_actions', label: 'Have something waiting', internal: true },
];

/** Where a tap on the link (the email's button, the notification, WhatsApp's button) opens their Connection app. */
export const CONNECTION_DELIVERY_OPENS = [
  { value: 'home', label: 'Their home screen' },
  { value: 'meetings', label: 'Their meetings (the schedule, for delegates)' },
  { value: 'venue', label: 'The venue map' },
];

/**
 * Every Connections email is sent from Bridged's verified events address, with the organiser's from
 * name and reply-to. Custom sender domains are not offered yet.
 */
export const CONNECTION_EMAIL_FROM_ADDRESS = 'no-reply@mail.bridged.events';

/** "Northway Roads Expo <no-reply@mail.bridged.events>". */
export const connectionEmailSender = fromName =>
  fromName ? `${fromName} <${CONNECTION_EMAIL_FROM_ADDRESS}>` : CONNECTION_EMAIL_FROM_ADDRESS;

/** "a, b and c", or "a, b, c and 5 more" when there are more than `shown`. */
export const formatAddressList = (addresses = [], shown = 3) => {
  const list = [...new Set((addresses || []).filter(Boolean))];
  if (list.length <= 1) return list[0] || '';
  if (list.length <= shown) return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  return `${list.slice(0, shown).join(', ')} and ${list.length - shown} more`;
};

export const CONNECTION_DELIVERY_CHANNELS = [
  {
    value: 'email',
    title: 'Email',
    description: `From ${CONNECTION_EMAIL_FROM_ADDRESS} with the event’s name, with a button to each person’s link.`,
    announcementDescription: `From ${CONNECTION_EMAIL_FROM_ADDRESS} with the event’s name.`,
    icon: Mail01Icon,
  },
  {
    value: 'whatsapp',
    title: 'WhatsApp',
    description: 'An approved template, to anyone with a mobile number.',
    icon: WhatsappIcon,
  },
  {
    value: 'push',
    title: 'App notification',
    description: 'To people signed in to the Connection app who allowed notifications.',
    icon: Notification01Icon,
  },
  {
    value: 'hubspot',
    title: 'HubSpot',
    description: 'Writes each person’s link to their HubSpot contact. Sends nothing.',
    icon: RefreshIcon,
    linkOnly: true,
    purposes: ['invite', 'reminder', 'results'],
    timings: ['pre_event'],
  },
  {
    value: 'qr',
    title: 'QR code on badge',
    description: 'A QR per person for the badge printer. Scanning it opens their own hub.',
    // With lead scanning on, the badge's link asks its owner for a code and saves a lead for anyone else.
    leadScanningDescription:
      'A QR per person for the badge printer. Another attendee who scans it saves a lead; its owner gets a code by email to open their Connection app.',
    icon: QrCodeIcon,
    linkOnly: true,
    purposes: ['invite', 'schedule'],
  },
];

/** Why a channel can't be used for a delivery's purpose and timing, or null when it can. */
export const connectionChannelBlockedReason = (channel, purpose, timing) => {
  const option = CONNECTION_DELIVERY_CHANNELS.find(item => item.value === channel);
  if (!option) return null;
  if (option.purposes && !option.purposes.includes(purpose)) {
    return purpose === 'announcement'
      ? 'Announcements have no personal link to write.'
      : 'Not used for this kind of delivery.';
  }
  if (option.timings && !option.timings.includes(timing)) return 'Before the event only.';
  return null;
};

/** A channel's line, as the agent's settings make it read (the badge QR with lead scanning on). */
/** A channel's line in Create delivery: an announcement has no personal link, so its email has no button. */
export const connectionChannelDescription = (option, agent, purpose) => {
  if (purpose === 'announcement' && option?.announcementDescription) return option.announcementDescription;
  return agent?.rules?.leadScanning === true && option?.leadScanningDescription
    ? option.leadScanningDescription
    : option?.description;
};

export const getConnectionDeliveryChannelLabel = value =>
  CONNECTION_DELIVERY_CHANNELS.find(channel => channel.value === value)?.title || value;

export const CONNECTION_DELIVERY_STATUS = {
  draft: { label: 'Draft', variant: 'secondary' },
  scheduled: { label: 'Scheduled', variant: 'sky' },
  sending: { label: 'Sending', variant: 'sky' },
  sent: { label: 'Sent', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'outline' },
};

// In the order the round is run: set up (People), match, meet, deliver, hear back, then report.
// Agenda shows only for formats that use the programme (see connectionOverviewTabs).
export const CONNECTION_OVERVIEW_TABS = [
  { value: 'overview', label: 'Overview', icon: DashboardSquare01Icon },
  { value: 'people', label: 'People', icon: UserMultiple02Icon },
  { value: 'companies', label: 'Companies', icon: Building03Icon, companies: true },
  { value: 'matches', label: 'Matches', icon: Agreement01Icon },
  { value: 'meetings', label: 'Meetings', icon: Calendar03Icon },
  { value: 'agenda', label: 'Agenda', icon: PresentationBarChart01Icon, programme: true },
  { value: 'deliveries', label: 'Deliveries', icon: Mail01Icon },
  { value: 'leads', label: 'Scans', icon: QrCodeIcon, leadScanning: true },
  { value: 'feedback', label: 'Feedback', icon: ThumbsUpIcon, feedback: true },
  { value: 'report', label: 'Report', icon: Analytics01Icon },
  { value: 'settings', label: 'Settings', icon: Settings02Icon },
];

/** Formats that put sessions in people's schedules, so the agent has an agenda. */
const AGENDA_FORMATS = ['one_to_many', 'session_recs', 'sessions'];

/**
 * The overview's tabs for an agent: Agenda only when one of its formats uses the programme,
 * Companies only when its rules group sponsors into companies, Scans only with lead scanning on,
 * Feedback only while the agent asks for it.
 */
export const connectionOverviewTabs = agent =>
  CONNECTION_OVERVIEW_TABS.filter(
    tab =>
      (!tab.programme ||
        (agent?.formats || []).some(slot =>
          AGENDA_FORMATS.includes(agentFormatOf(slot?.format))
        )) &&
      (!tab.companies || Boolean(agent?.rules?.companies)) &&
      (!tab.leadScanning || agent?.rules?.leadScanning === true) &&
      (!tab.feedback || agent?.rules?.feedback !== false)
  );
